import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  useTheme,
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  Grid,
  Container,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import FormTextField from "../components/form/FormTextField";
import Backdrop from "../components/common/Backdrop";
import { useLoginMutation } from "../features/api/authApiSlice";
import { setCredentials } from "../features/slices/authSlice";
import { selectCurrentToken } from "../features/slices/authSlice";
import { setAlert } from "../features/slices/alertSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import Image from "../components/common/Image";
import { useTranslation } from "react-i18next";
import ImageCarousel from "../components/common/ImageCarousel";
import * as Constant from "../features/constant";
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
  useAddFingerprintMutation,
} from "../features/api/publicApiSlice";
import { lighten, darken, alpha } from "@material-ui/core";
import FormPhoneNumber from "../components/form/FormPhoneNumber";

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const token = useSelector(selectCurrentToken);
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const lang = localStorage.getItem("language");
  const loginLight = lighten(theme.palette.button.login, 0.1);
  const loginDark = darken(theme.palette.button.login, 0.05);
  const [loginType, setLoginType] = useState("username"); 

  // const bonusSliderImage_en = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  // const bonusSliderImage_bd = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  const BONUS_SLIDER_BASE = process.env.REACT_APP_BONUS_SLIDER || "";

  function getBonusSliderImages(envVar) {
    if (!envVar) return [];
    return envVar.split(",").map(function (fileName) {
      return BONUS_SLIDER_BASE + fileName.trim();
    });
  }

  const bonusSliderImage_en = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_EN);
  const bonusSliderImage_bd = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_BD);

  const loginSchema = yup.object().shape({
    username: yup.string().when([], {
      is: () => loginType === "username",
      then: (schema) => schema.required(t("validation.username")),
    }),
    mobile: yup.string().when([], {
      is: () => loginType === "mobile",
      then: (schema) => schema.required(t("validation.mobilenum")),
    }),
    email: yup.string().when([], {
      is: () => loginType === "email",
      then: (schema) => schema.required(t("validation.emailisrequired")),
    }),
    password: yup.string().required(t("validation.password")),
  });

  // utilize the other hooks to display loader, display error message or anything
  const [login, { data, isLoading, isError, isSuccess, error, reset }] =
    useLoginMutation();

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(loginSchema),
  });

  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();

    return result;
  };

  const [addFingerprint, { fingerprintData }] =
    useAddFingerprintMutation();


  useEffect(() => {
    if (token) {
      navigate(Constant.getPath("/"));
    }
  }, []);

  const submitForm = async (data) => {
    let formData = new FormData();
    if (loginType === "username")
      formData.append("username", data.username);
    else if (loginType === "mobile"){
      const finalMobileNumber =
      data?.mobile?.replace(/[^A-Z0-9]+/gi, "");
      formData.append("username", finalMobileNumber);
    }
    else if (loginType === "email")
      formData.append("username", data.email);
    formData.append("password", data.password);
    formData.append("grant_type", "password");
    formData.append("login_type", loginType);

    const userData = await login(formData).unwrap();
    dispatch(setCredentials({ ...userData }));

    const fingerprint = await getFingerprint();

    await addFingerprint({
      fingerprint: fingerprint.visitorId,
      userAgent: navigator.userAgent,
    });
  };

  if (!isLoading && isSuccess) {

    window.loginListener = window.loginListener || window;

    dispatch(
      setAlert({
        isOpen: true,
        message: t("header.loginsuccessmessage"),
        variant: "success",
      })
    );
    navigate(Constant.getPath("/"));
    reset();

    const loginCompleteMessage = { event: 'af_login' };
    window.loginListener.postMessage(JSON.stringify(loginCompleteMessage));
  }

  if (!isLoading && isError) {
    dispatch(
      setAlert({
        isOpen: true,
        message: error.data.error_description,
        variant: "error",
      })
    );
    reset();
  }

  const getTypeLabel = (type) => {
    switch (type) {
      case "mobile":
        return t("mobileloginregister.mobilenumber"); 
      case "username":
        return t("mobileloginregister.username");
      case "email":
        return t("mobileloginregister.email");
      default:
        return type;
    }
  };

  return (
    <Box
      sx={{
        backgroundColor:
        process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY ||
        theme.palette.background.paper,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        ...(mobileView ? { pt: { xs: '56px', sm: '64px' } } : {}),
      }}
    >
      <Box>
        <ImageCarousel
          banners={lang === "en" ? bonusSliderImage_en : bonusSliderImage_bd}
          navigation={false}
          spaceBetween={0}
        />
      </Box>
  
      <Typography 
      variant="h6"
        sx={{
          textAlign: 'center',
          marginTop: 2,
          fontWeight: 'bold',
          color: process.env.REACT_APP_COLOR_PRIMARY,
          textTransform: 'uppercase',
        }}>
        {t("mobileloginregister.login")}
      </Typography>
      <Box
        sx={{
          backgroundColor:
            process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY ||
            theme.palette.background.paper,
          flex: 1, // ← this fills remaining space dynamically
          display: "flex-start",
          flexDirection: "column",
          justifyContent: "center",
          pb: 3,
          px: 2,
        }}
      >

        <form onSubmit={handleSubmit(submitForm)}>
          {/* Tabs */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          {["username", "mobile", "email"].map((type) => (
            <Box
              key={type}
              onClick={() => setLoginType(type)}
              sx={{
                mx: 2,
                pb: 0.5,
                cursor: "pointer",
                borderBottom: loginType === type ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: loginType === type
                    ? theme.palette.primary.main
                    : process.env.REACT_APP_COLOR_TEXT || "white" ,
                  fontWeight: loginType === type ? "bold" : "normal",
                  textTransform: "capitalize",
                }}
              >
                {getTypeLabel(type)}
                </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            backgroundColor: process.env.REACT_APP_IS_LIGHT_THEME === 'true' 
            ? 'rgba(0, 0, 0, 0.05)'  // darken for light theme
            : 'rgba(255, 255, 255, 0.05)', // lighten for dark theme
            borderRadius: 2, // rounded corners
            px: 2,
            marginTop: 2,
            paddingBottom: 3,
            paddingTop: 1,
            backdropFilter: 'blur(6px)',
            border: process.env.REACT_APP_IS_LIGHT_THEME === 'true' ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.15)' ,
            // boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
            borderRadius: '10px',
            
          }}
        >
          
          {/* <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || undefined,
                mt: 2,
                mr: 1,
              }}
            />
            <Box flex={1}>
              <FormTextField
                label={t("mobileloginregister.username")}
                control={control}
                controllerName="username"
                size="small"
                // sx={{
                //   backgroundColor: process.env.REACT_APP_COLOR_PROFILE_BACKGROUND
                // }}
              />
            </Box>
          </Box> */}

          {/* Dynamic Input Field according to Login Type */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <PersonIcon
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || undefined,
                mt: 2,
                mr: 1,
              }}
            />
            <Box flex={1}>
              {loginType === "username" && (
                <FormTextField
                  label={t("mobileloginregister.username")}
                  // placeholder="Enter Your Username"
                  control={control}
                  controllerName="username"
                  size="small"
                />
              )}
              {loginType === "mobile" && (
                <FormPhoneNumber
                  label={t("mobileloginregister.mobilenumber")}
                  // placeholder="Enter Your Mobile Number"
                  control={control}
                  controllerName="mobile"
                  size="small"
                  sx={{marginTop: -1000}}
                />
              )}
              {loginType === "email" && (
                <FormTextField
                  label={t("mobileloginregister.email")}
                  // placeholder="Enter Your Email"
                  control={control}
                  controllerName="email"
                  type="email"
                  size="small"
                />
              )}
            </Box>
          </Box>
  
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <KeyIcon
              sx={{
                color: process.env.REACT_APP_COLOR_TEXT || undefined,
                mt: 2,
                mr: 1,
              }}
            />
            <Box flex={1}>
              <FormTextField
                label={t("mobileloginregister.password")}
                control={control}
                controllerName="password"
                type="password"
                showPasswordToggle
                size="small"
                // sx={{
                //   backgroundColor: process.env.REACT_APP_COLOR_PROFILE_BACKGROUND
                // }}
              />
            </Box>
          </Box>
          </Box>
          <Box display="flex" justifyContent="center" mt={isError ? "10px" : "30px"}>
            <Button variant="contained" type="submit"
            sx={{
              // color: process.env.REACT_APP_COLOR_TEXT,
              background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
              // fontWeight: 'bold',
              px: 4,
              py: 1,
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              },
            }}
            >
              {t("mobileloginregister.login")}
            </Button>
          </Box>
  
          <Box display="flex" justifyContent="center" alignItems="center">
            <a style={{ color: theme.palette.primary.main }} href="/forgotPassword">
              <Typography mt={2} fontSize={14}>
                {t("mobileloginregister.forgotpassword")}
              </Typography>
            </a>
          </Box>
  
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography
              mt={2}
              fontSize={14}
              sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
            >
              {t("mobileloginregister.contactus")}
              <a style={{ color: theme.palette.primary.main }} href="/ContactUs">
                {t("mobileloginregister.customerservice")}
              </a>
              {"."}
            </Typography>
          </Box>
        </form>
      </Box>
    </Box>
  );
  
};

export default Login;
